// ⚠ DO NOT change!!! Long imports are intentional here
import { getSettingsValues } from '@wix/tpa-settings/dist/src/contexts/SettingsContext/getSettingsValues';
import {
  ConsoleLogger,
  LogLevel,
} from '@wix/social-groups-common/dist/src/loggers';
import { BaseWidgetController } from '@wix/social-groups-common/dist/src/controllers/BaseWidgetController';
import {
  I$WWrapper,
  IAppData,
  IControllerConfig,
  IWidgetControllerConfig,
  IWixAPI,
} from '@wix/native-components-infra/dist/src/types/types';
import Experiments, { ExperimentsBag } from '@wix/wix-experiments';
import { GroupsWidgetProps } from '../../components/App/types';

import { GROUP_LIST } from '../../config/constants';
import {
  BiLoggerController,
  TranslationsController,
} from '@wix/social-groups-common/dist/src/controllers';
import { UpdateProgress } from '@wix/social-groups-common/dist/src/components/ContentEditor/UpdateProgress';

import { ISettingsParams, settingsParams } from '../../settings/settings';

export abstract class Controller<
  T extends GroupsWidgetProps
> extends BaseWidgetController<T> {
  protected controllers: BaseWidgetController<any>[];
  protected wixCodeApi: IWixAPI;
  protected appParams: IAppData;
  protected config: IControllerConfig;
  protected experiments: Experiments;

  protected constructor(
    controllerConfig: IWidgetControllerConfig,
    experiments: ExperimentsBag = {},
  ) {
    super(controllerConfig);
    this.experiments = new Experiments({ experiments });

    const { appParams, wixCodeApi, config } = controllerConfig;

    this.wixCodeApi = wixCodeApi;
    this.appParams = appParams;
    this.config = config;
    this.controllers = [
      new TranslationsController(controllerConfig, this.getTranslationsUrl()),
      new BiLoggerController(controllerConfig),
    ];
    this.setLogger(controllerConfig);
    this.onUserLogin(async () => {
      this.setState({
        currentUser: this.getCurrentUser(),
      } as T);
    });
  }

  bootstrapSettings(config: IControllerConfig) {
    return getSettingsValues<ISettingsParams>(
      config.publicData.COMPONENT || {},
      settingsParams,
    );
  }

  updateConfig($w, updatedConfig) {
    this.config = updatedConfig;

    this.setState({
      styleParams: updatedConfig.style.styleParams,
      appSettings: this.bootstrapSettings(updatedConfig),
    } as T);

    this.controllers.map((ctrl) => ctrl.updateConfig($w, updatedConfig));
  }

  async pageReady($w?: I$WWrapper, wixAPI?: IWixAPI) {
    // Props for SSR
    await this.setInitialAppState();
    this.controllers.map((ctrl) => ctrl.pageReady($w, wixAPI));
  }

  protected async setInitialAppState() {
    const initialProps = await this.getInitialProps();
    this.setState(initialProps);
  }

  async getInitialProps() {
    const initialProps = {
      experiments: this.experiments.all(),
      cssBaseUrl: this.appParams.baseUrls.staticsBaseUrl,
      currentUser: this.getCurrentUser(),
      locale: this.getLocale(),
      styleParams: this.config.style.styleParams,
      appSettings: this.bootstrapSettings(this.config),
      language: this.getSiteLanguage(),
      updateProgress: UpdateProgress.UPDATING,
      reportAppLoaded: this.reportAppLoaded,
    } as Partial<T>;

    try {
      const controllersProps = await Promise.all(
        this.controllers.map((ctrl) => ctrl.getInitialProps()),
      );
      const flattenProps = controllersProps.reduce(
        (acc, props) => ({ ...acc, ...props }),
        {},
      );
      Object.assign(initialProps, flattenProps);
    } catch (e) {
      console.error('Get Initial Controllers Props: FAIL');
    }
    return initialProps;
  }

  exports() {
    return {};
  }

  protected setLogger(controllerConfig: IWidgetControllerConfig) {
    ConsoleLogger.mode = this.getLogLevel(controllerConfig);
  }

  private getLogLevel(controllerConfig: IWidgetControllerConfig) {
    let logLevel = LogLevel.NONE;
    try {
      if (controllerConfig.wixCodeApi.location.query.debug === 'true') {
        logLevel = LogLevel.LOG;
      }
      if (controllerConfig.wixCodeApi.location.query.trace === 'true') {
        logLevel = LogLevel.TRACE;
      }
    } catch (e) {}
    return logLevel;
  }

  getTranslationsUrl(): string {
    const baseUrl = this.getBaseUrls().staticsBaseUrl;
    const language = this.getSiteLanguage();
    return `${baseUrl}assets/locales/${GROUP_LIST}_${language}.json`;
  }

  private readonly reportAppLoaded = () => {
    const fedopsLogger = this.getFedopsLogger();
    fedopsLogger.appLoaded();
  };
}
